import db from "./db";
import { API_BASE_URL, authFetch } from "../config/api";

// ── helpers ───────────────────────────────────────────────────────────────────

// Normalise a MongoDB document — converts _id and student_id to strings
const normaliseDoc = (d) => {
  const doc = { ...d, _id: String(d._id) };
  if (doc.student_id) doc.student_id = String(doc.student_id);
  return doc;
};

const normalise = (docs) => docs.map(normaliseDoc);

// Sessions need deeper normalisation — stringify class._id and student refs
const normaliseSessions = (docs) =>
  docs.map((s) => ({
    ...s,
    _id: String(s._id),
    classes: (s.classes || []).map((c) => ({
      ...c,
      _id: String(c._id),
      students: (c.students || []).map(String),
    })),
    externals: (s.externals || []).map((e) =>
      typeof e === "object" && e !== null
        ? { ...e, _id: String(e._id) }
        : String(e),
    ),
  }));

// ── individual collection syncs ───────────────────────────────────────────────

async function syncSessions(onProgress) {
  onProgress("Syncing sessions…", 0);
  const res = await authFetch(`${API_BASE_URL}/api/sync/sessions`);
  if (!res.ok) throw new Error("Failed to fetch sessions");
  const json = await res.json();
  const docs = normaliseSessions(json.data || []);
  await db.sessions.clear();
  await db.sessions.bulkPut(docs);
  onProgress("Sessions synced", 1);
}

async function syncStudents(onProgress) {
  onProgress("Syncing students…", 0);
  const res = await authFetch(`${API_BASE_URL}/api/sync/students`);
  if (!res.ok) throw new Error("Failed to fetch students");
  const json = await res.json();
  const docs = normalise(json.data || []);
  await db.students.clear();
  await db.students.bulkPut(docs);
  onProgress("Students synced", 1);
}

async function syncResults(onProgress) {
  const statusRes = await authFetch(`${API_BASE_URL}/api/sync/status`);
  if (!statusRes.ok) throw new Error("Failed to get sync status");
  const status = await statusRes.json();
  const totalPages = status.resultPages || 1;

  await db.results.clear();

  for (let page = 0; page < totalPages; page++) {
    onProgress(
      `Syncing results… page ${page + 1} of ${totalPages}`,
      page / totalPages,
    );
    const res = await authFetch(
      `${API_BASE_URL}/api/sync/results?page=${page}`,
    );
    if (!res.ok) throw new Error(`Failed to fetch results page ${page}`);
    const json = await res.json();
    const docs = normalise(json.data || []);
    await db.results.bulkPut(docs);
    if (!json.hasMore) break;
  }
  onProgress("Results synced", 1);
}

async function syncCatalogue(onProgress) {
  onProgress("Syncing catalogue…", 0);
  const res = await authFetch(`${API_BASE_URL}/api/sync/catalogue`);
  if (!res.ok) throw new Error("Failed to fetch catalogue");
  const json = await res.json();
  const courses = normalise(json.courses || []);
  const departments = normalise(json.departments || []);
  await db.courses.clear();
  await db.departments.clear();
  await db.courses.bulkPut(courses);
  await db.departments.bulkPut(departments);
  onProgress("Catalogue synced", 1);
}

// ── main sync orchestrator ────────────────────────────────────────────────────

export async function runFullSync(onStep, onError) {
  const steps = [syncCatalogue, syncSessions, syncStudents, syncResults];
  const total = steps.length;
  try {
    for (let i = 0; i < steps.length; i++) {
      await steps[i]((msg, progress) => {
        onStep(msg, i, total, progress);
      });
    }
    await db.sync_meta.put({ key: "last_sync", value: Date.now() });
    onStep("Sync complete", total, total, 1);
    return true;
  } catch (err) {
    console.error("[syncManager]", err);
    onError(err);
    return false;
  }
}

// ── metadata helpers ──────────────────────────────────────────────────────────

export async function getLastSyncTime() {
  const meta = await db.sync_meta.get("last_sync");
  return meta?.value || null;
}

export async function isSyncedDataAvailable() {
  const count = await db.sessions.count();
  return count > 0;
}
